import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Project } from "@/data/projects";
import { techLogos } from "@/data/techLogos";
import { ProjectMedia } from "./ProjectMedia";
import { StatusPill } from "./StatusPill";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { projectCardCopy } from "@/lib/siteCopy";

interface ProjectCardProps {
  project: Project;
  index: number;
  /** Renders the wide, split image+content layout instead of the standard stacked card */
  featured?: boolean;
}

export function ProjectCard({ project, index, featured = false }: ProjectCardProps) {
  const lang = useSiteLanguage();
  const statusLabel = project.liveUrl
    ? projectCardCopy[lang].live
    : projectCardCopy[lang].caseStudy;
  const visibleTech = project.techStack.slice(0, 4);
  const extraTech = project.techStack.length - visibleTech.length;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16, transition: { duration: 0.25 } }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, delay: (index % 6) * 0.06, ease: [0.22, 1, 0.36, 1] }}
      className={featured ? "col-span-full" : ""}
    >
      <Link
        to={`/work/${project.id}`}
        className={`group relative flex overflow-hidden rounded-2xl border border-border/50 bg-card transition-colors hover:border-foreground/30 ${
          featured ? "flex-col lg:flex-row" : "flex-col"
        }`}
      >
        <div
          className={`relative overflow-hidden bg-muted/30 ${
            featured ? "aspect-[16/10] lg:aspect-auto lg:w-[55%]" : "aspect-[4/3]"
          }`}
        >
          <ProjectMedia
            image={project.image}
            video={project.video}
            alt={project.title}
            playOnHover
            mediaClassName="transition-transform duration-700 ease-out group-hover:scale-[1.04]"
          />

          <div className="absolute left-4 top-4">
            <StatusPill label={statusLabel} color={project.accentColor} />
          </div>

          {project.logo && (
            <div className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-background/90 p-1.5 shadow-sm backdrop-blur-sm">
              <img src={project.logo} alt="" className="h-full w-full object-contain" />
            </div>
          )}
        </div>

        <div className={`flex flex-1 flex-col justify-between p-6 lg:p-8 ${featured ? "lg:max-w-lg" : ""}`}>
          <div>
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              <span>{project.category}</span>
              <span className="text-border">/</span>
              <span>{project.year}</span>
            </div>
            <h3
              className={`mt-3 font-bold tracking-tight text-foreground ${
                featured ? "text-2xl lg:text-3xl" : "text-xl"
              }`}
            >
              {project.title}
            </h3>
            <p className={`mt-3 text-muted-foreground ${featured ? "text-base" : "text-sm line-clamp-2"}`}>
              {project.description}
            </p>
          </div>

          <div className="mt-6 flex items-center justify-between border-t border-border/50 pt-4">
            <div className="flex -space-x-2">
              {visibleTech.map((tech) => {
                const logo = techLogos[tech];
                if (!logo) return null;
                return (
                  <div
                    key={tech}
                    title={tech}
                    className="h-7 w-7 overflow-hidden rounded-full border-2 border-card bg-white"
                  >
                    <img src={logo} alt={tech} className="h-full w-full object-contain" />
                  </div>
                );
              })}
              {extraTech > 0 && (
                <div className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-card bg-muted text-[10px] font-medium">
                  +{extraTech}
                </div>
              )}
            </div>

            <span className="flex items-center gap-1 font-mono text-xs text-foreground transition-transform duration-300 group-hover:translate-x-1">
              {projectCardCopy[lang].view}
              <ArrowUpRight className="h-3.5 w-3.5" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}